import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  Play,
  BookOpen,
  Target,
  Eye,
  TrendingUp,
  RotateCcw,
  Share2,
  MessageCircle,
  Heart,
  Compass,
  Star,
  Trophy,
  Zap,
  BarChart3,
  Brain,
  Layers,
  Calendar,
  Globe,
  Hammer,
  HeartHandshake,
  Flame,
} from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';
import VideoModal from '../components/VideoModal';

/* Section header used inside white sections — eyebrow + headline,
   styled to match the rest of the site. */
const SectionHead = ({ eyebrow, title, italic, sub }) => (
  <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
    {eyebrow && (
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
        {eyebrow}
      </p>
    )}
    <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a]">
      {title}{' '}
      {italic && <em className="italic font-light text-blue-600">{italic}</em>}
    </h2>
    {sub && (
      <p className="mt-5 text-lg text-[#0b1f3a]/70 leading-relaxed">{sub}</p>
    )}
  </div>
);

const PRINCIPLES = [
  ['Consistency beats intensity.', 'Small steps daily outperform big bursts.'],
  ['Action beats consumption.', 'Progress comes from doing, not watching.'],
  ['Progress beats perfection.', 'The goal is forward movement, not flawless execution.'],
];

const WORLDS = [
  {
    name: 'Foundation',
    Icon: Brain,
    gradient: 'from-emerald-400 to-teal-600',
    body:
      'This world focuses on your inner world — the way you think and the way you see yourself. It covers beliefs, mindset, self-worth, identity, and the internal patterns that shape your decisions. It\u2019s where you build clarity, emotional stability, and a stronger relationship with yourself.',
  },
  {
    name: 'Creation',
    Icon: Hammer,
    gradient: 'from-amber-400 to-orange-600',
    body:
      'This world focuses on the outward side of life — building, producing, creating results. It covers work and business thinking, time management, money management, execution, and the skills required to build a strong life in the real world. It\u2019s about turning effort into progress and ideas into reality.',
  },
  {
    name: 'Connection',
    Icon: HeartHandshake,
    gradient: 'from-sky-400 to-blue-600',
    body:
      'This world focuses on people and relationships. It includes communication, influence, social skills, partnership, parenting, leadership in relationships, and building strong connections with others. It\u2019s about becoming someone people trust, respect, and feel safe around.',
  },
  {
    name: 'Fuel',
    Icon: Flame,
    gradient: 'from-fuchsia-400 to-purple-600',
    body:
      'This world focuses on what gives life meaning and energy. It includes hobbies, experiences, travel, joy, curiosity, and the moments that remind you why growth matters. It\u2019s about living — not just improving. The goal is to build a life you actually enjoy.',
  },
];

const DAILY_STEPS = [
  { Icon: BookOpen, color: 'bg-blue-500', name: 'Learning', body: 'Discovering a new idea, concept, or skill.' },
  { Icon: Target, color: 'bg-emerald-500', name: 'Practice', body: 'Applying that skill through a small real-life action.' },
  { Icon: Eye, color: 'bg-violet-500', name: 'Reflection', body: 'Observing the result and understanding what worked.' },
  { Icon: TrendingUp, color: 'bg-amber-500', name: 'Tracking', body: 'Recording progress and moving forward to the next step.' },
];

const COMMUNITY = [
  { Icon: Share2, color: 'bg-blue-100 text-blue-600', title: 'Share progress', body: 'Members can document their development and milestones as they move through skills and challenges.' },
  { Icon: MessageCircle, color: 'bg-emerald-100 text-emerald-600', title: 'Exchange experience', body: 'People can share insights, practical ideas, and lessons learned from their own journey.' },
  { Icon: Heart, color: 'bg-pink-100 text-pink-600', title: 'Encourage each other', body: 'Seeing others progress often creates motivation and accountability.' },
  { Icon: Compass, color: 'bg-violet-100 text-violet-600', title: 'Grow in the same direction', body: 'The environment brings together people who are intentionally working on improving different areas of their lives.' },
];

const REINFORCEMENT = [
  { Icon: Zap, color: 'from-amber-400 to-yellow-500', title: 'Experience points', body: 'Points earned through completing skills, exercises, and activities.' },
  { Icon: Star, color: 'from-blue-400 to-blue-600', title: 'Levels', body: 'Levels represent overall progression within the system.' },
  { Icon: Trophy, color: 'from-violet-400 to-purple-600', title: 'Achievements', body: 'Milestones that mark meaningful moments in the journey.' },
  { Icon: BarChart3, color: 'from-emerald-400 to-teal-600', title: 'Progress tracking', body: 'Clear indicators showing how far you\u2019ve progressed within a skill.' },
];

const JOURNEY_STEPS = [
  { Icon: Brain, color: 'bg-blue-100 text-blue-600', label: 'Philosophy' },
  { Icon: Layers, color: 'bg-emerald-100 text-emerald-600', label: 'Worlds' },
  { Icon: TrendingUp, color: 'bg-violet-100 text-violet-600', label: 'Progression' },
  { Icon: Calendar, color: 'bg-amber-100 text-amber-600', label: 'Daily Rhythm' },
  { Icon: Globe, color: 'bg-pink-100 text-pink-600', label: 'Community' },
];

const HowItWorks = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-24 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="hiw-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            How Levanta works
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            The system,{' '}
            <em className="italic font-light text-blue-600">explained.</em>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Four Worlds. Structured skill paths. A daily cycle of Learning,
            Practice, Reflection and Tracking. Visible progress. A shared
            Community. The whole architecture, in one read.
          </p>
          <div className="mt-9">
            <button
              type="button"
              onClick={() => setIsVideoOpen(true)}
              data-testid="watch-presentation-btn"
              className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#0b1f3a] text-white font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              <Play className="w-4 h-4 fill-current" />
              Watch presentation video
            </button>
          </div>
        </div>
      </section>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://Levanta.b-cdn.net/levanta_presentation_v1%20(720p).mp4"
        title="Levanta Presentation"
      />

      {/* QUICK ANSWER */}
      <section className="px-6 py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-3xl bg-[#fafbfd] ring-1 ring-black/5 p-7 md:p-9">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3">
              How Levanta works in 3 sentences
            </p>
            <p className="text-lg md:text-xl text-[#0b1f3a]/85 leading-relaxed">
              Levanta organises personal growth into four Worlds — Foundation,
              Creation, Connection and Fuel — each containing structured skill
              paths. Members progress through a daily cycle of Learning,
              Practice, Reflection and Tracking, so development becomes a small
              daily habit instead of an occasional burst of motivation. Visible
              progress, a shared Community and a clear system replace
              inspiration with consistency.
            </p>
          </div>
        </div>
      </section>

      {/* ===== 01 · PHILOSOPHY ===== */}
      <section className="px-6 py-20 md:py-24 bg-[#f4f8fc]">
        <div className="max-w-6xl mx-auto">
          <SectionHead
            eyebrow="Section 01 · Philosophy"
            title="Growth isn’t motivation."
            italic="It’s a system."
            sub="Most people don’t fail because they’re weak. They fail because they have no structure to follow when motivation drops."
          />

          {/* 3 principles */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {PRINCIPLES.map(([head, tail]) => (
              <div
                key={head}
                className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <p className="text-2xl md:text-3xl font-black leading-tight text-[#0b1f3a]">
                  <em className="italic font-light text-blue-600">{head.replace('.', '')}.</em>
                </p>
                <p className="mt-3 text-[#0b1f3a]/65 leading-relaxed">{tail}</p>
              </div>
            ))}
          </div>

          {/* Old vs Levanta */}
          <div className="grid md:grid-cols-2 gap-5 mb-12">
            <div className="rounded-3xl bg-white ring-1 ring-black/5 p-7">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-500 mb-4 text-center">
                The old way
              </p>
              <div className="flex items-center justify-center flex-wrap gap-2 text-[#0b1f3a]/55">
                <span>Inspiration</span>
                <ArrowRight className="w-4 h-4 text-rose-300" />
                <span>Big effort</span>
                <ArrowRight className="w-4 h-4 text-rose-300" />
                <span>Burnout</span>
                <ArrowRight className="w-4 h-4 text-rose-300" />
                <span className="text-rose-500 font-bold">Quit</span>
              </div>
            </div>
            <div className="rounded-3xl bg-[#0b1f3a] text-white p-7 ring-1 ring-[#0b1f3a]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-4 text-center">
                The Levanta way
              </p>
              <div className="flex items-center justify-center flex-wrap gap-2">
                <span>Small steps</span>
                <ArrowRight className="w-4 h-4 text-blue-300" />
                <span>Repetition</span>
                <ArrowRight className="w-4 h-4 text-blue-300" />
                <span>Momentum</span>
                <ArrowRight className="w-4 h-4 text-blue-300" />
                <span className="text-blue-300 font-bold">Upgrade</span>
              </div>
            </div>
          </div>

          {/* Proof + closing */}
          <p className="text-2xl md:text-3xl font-bold text-[#0b1f3a] text-center max-w-3xl mx-auto leading-snug">
            If your plan only works when you feel motivated,{' '}
            <em className="italic font-light text-blue-600">it&rsquo;s not a plan.</em>
          </p>
          <p className="mt-6 text-[#0b1f3a]/65 text-center max-w-2xl mx-auto">
            That&rsquo;s why Levanta is built as a progression system — not a content library.
          </p>
        </div>
      </section>

      {/* ===== 02 · STRUCTURE (WORLDS) ===== */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <SectionHead
            eyebrow="Section 02 · Structure"
            title="The journey is organized"
            italic="into worlds."
          />

          <div className="max-w-3xl mx-auto space-y-5 text-[#0b1f3a]/80 text-lg leading-relaxed mb-14">
            <p>Levanta organizes personal development into distinct environments called Worlds.</p>
            <p>
              Each world focuses on a different area of growth and contains its own collection of
              skills. These skills are divided into smaller sub-skills and practical exercises so
              they can be learned and practiced step by step.
            </p>
            <p>
              This approach keeps learning clear and focused. Instead of trying to improve
              everything at once, members can explore specific areas and develop real capabilities
              over time.
            </p>
            <p>
              Inside each world, skills progress through stages. Every skill is built from smaller
              parts, and those parts gradually develop into stronger abilities through practice and
              repetition.
            </p>
          </div>

          {/* The 4 worlds — homepage-style cards (Brain/Hammer/HeartHandshake/Flame). */}
          <div className="grid sm:grid-cols-2 gap-5">
            {WORLDS.map((w) => (
              <div key={w.name} className="group">
                <div
                  className={`relative h-44 rounded-3xl bg-gradient-to-br ${w.gradient} p-6 md:p-7 flex flex-col justify-between overflow-hidden transition-transform duration-300 group-hover:-translate-y-1`}
                >
                  <w.Icon
                    className="absolute -right-4 -bottom-4 w-32 h-32 text-white/15 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <div className="flex items-start justify-between relative z-10">
                    <span className="text-xs tracking-widest uppercase text-white/80">World</span>
                    <span className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <w.Icon className="w-4 h-4 text-white" strokeWidth={2.25} aria-hidden="true" />
                    </span>
                  </div>
                  <h3 className="relative z-10 text-3xl font-black text-white">{w.name}</h3>
                </div>
                <p className="mt-4 text-[#0b1f3a]/75 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-center text-[#0b1f3a]/65 max-w-3xl mx-auto leading-relaxed">
            Each world contains its own set of skills, and each skill unfolds through smaller stages
            of development. This creates a clear path where growth happens gradually, through
            practice and real experience.
          </p>
        </div>
      </section>

      {/* ===== 03 · MECHANISM (PROGRESSION) ===== */}
      <section className="px-6 py-20 md:py-24 bg-[#f4f8fc]">
        <div className="max-w-6xl mx-auto">
          <SectionHead
            eyebrow="Section 03 · Mechanism"
            title="Progress happens"
            italic="step by step."
          />

          <div className="max-w-3xl mx-auto space-y-5 text-[#0b1f3a]/80 text-lg leading-relaxed mb-14">
            <p>Inside every world, development is organized through a progression system.</p>
            <p>
              Instead of jumping between topics or consuming endless content, members move through
              clearly defined skills. Each skill is divided into smaller parts so it can be
              practiced and developed gradually.
            </p>
            <p className="font-bold text-[#0b1f3a] text-xl">
              Progress is not based on how much content you watch — it is based on{' '}
              <em className="italic font-light text-blue-600">implementation.</em>
            </p>
          </div>

          {/* Two-up: progression flow + skill tree visual */}
          <div className="grid lg:grid-cols-2 gap-6 mb-12">
            {/* Flow */}
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3">
                How progression works
              </p>
              <h3 className="text-2xl font-black text-[#0b1f3a] mb-5 leading-tight">
                Every skill follows a{' '}
                <em className="italic font-light text-blue-600">simple structure.</em>
              </h3>

              <div className="rounded-2xl bg-[#0b1f3a] text-white p-5 mb-5">
                <div className="flex flex-wrap items-center justify-center gap-2 text-sm font-semibold">
                  <span className="bg-blue-500 px-3 py-2 rounded-lg">Skill</span>
                  <ArrowRight className="w-4 h-4 text-blue-300" />
                  <span className="bg-blue-400 px-3 py-2 rounded-lg">Sub-skills</span>
                  <ArrowRight className="w-4 h-4 text-blue-300" />
                  <span className="bg-blue-400 px-3 py-2 rounded-lg">Practice</span>
                  <ArrowRight className="w-4 h-4 text-blue-300" />
                  <span className="bg-blue-600 px-3 py-2 rounded-lg">Progress</span>
                </div>
              </div>

              <p className="text-[#0b1f3a]/70 leading-relaxed mb-3">
                A skill is broken into smaller elements so it becomes easier to learn and apply. As
                each part improves, the overall ability becomes stronger.
              </p>
              <p className="text-[#0b1f3a]/70 leading-relaxed">
                This creates a clear path where development happens naturally through repetition
                and experience.
              </p>
            </div>

            {/* Skill tree visual */}
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3">
                Layer by layer
              </p>
              <h3 className="text-2xl font-black text-[#0b1f3a] mb-5 leading-tight">
                Growth is{' '}
                <em className="italic font-light text-blue-600">built up.</em>
              </h3>

              <div className="flex flex-col items-center py-4">
                <div className="bg-[#0b1f3a] text-white font-bold px-6 py-3 rounded-xl">Skill</div>
                <ChevronDown className="w-6 h-6 text-blue-400 my-1" />
                <div className="flex gap-3 my-1 flex-wrap justify-center">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-blue-500 text-white font-semibold px-4 py-2 rounded-lg text-sm">
                      Sub-skill {i}
                    </div>
                  ))}
                </div>
                <ChevronDown className="w-6 h-6 text-blue-400 my-1" />
                <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold px-6 py-3 rounded-xl">
                  Mastery
                </div>
              </div>
            </div>
          </div>

          {/* Stages */}
          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-9">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3">
              Learning through stages
            </p>
            <h3 className="text-2xl font-black text-[#0b1f3a] mb-7 leading-tight">
              Each skill moves through{' '}
              <em className="italic font-light text-blue-600">stages of development.</em>
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                'At the beginning, you learn the concept.',
                'Then you practice it in small actions.',
                'With repetition, the skill becomes more natural and reliable.',
              ].map((step, i) => (
                <div key={i} className="rounded-2xl bg-[#fafbfd] p-6 text-center ring-1 ring-black/5">
                  <p className="text-3xl font-black text-blue-600 mb-3 font-mono">
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <p className="text-[#0b1f3a]/75 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
            <p className="mt-7 text-[#0b1f3a]/65 text-center">
              Over time, what once required effort becomes part of how you operate.
            </p>
          </div>

          <p className="mt-10 text-center text-[#0b1f3a]/65 max-w-3xl mx-auto leading-relaxed">
            Levanta turns personal development into a structured progression — where improvement
            happens through small, consistent steps that build into real capability.
          </p>
        </div>
      </section>

      {/* ===== 04 · HABIT LAYER (DAILY RHYTHM) ===== */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <SectionHead
            eyebrow="Section 04 · Daily Rhythm"
            title="Growth happens through"
            italic="small daily actions."
          />

          <div className="max-w-3xl mx-auto space-y-5 text-[#0b1f3a]/80 text-lg leading-relaxed mb-14">
            <p className="font-bold text-[#0b1f3a]">
              Levanta is not designed for occasional bursts of motivation.
            </p>
            <p>
              It works through small, repeatable actions that gradually build stronger habits and
              skills.
            </p>
            <p>
              Instead of overwhelming members with long lessons or heavy schedules, the system
              encourages short daily engagement that fits naturally into everyday life.
            </p>
            <p className="font-bold text-[#0b1f3a]">
              Progress happens through{' '}
              <em className="italic font-light text-blue-600">consistency, not intensity.</em>
            </p>
          </div>

          {/* 4 daily steps */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {DAILY_STEPS.map(({ Icon, color, name, body }, i) => (
              <div
                key={name}
                className="rounded-3xl bg-[#fafbfd] ring-1 ring-black/5 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:bg-white"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-10 h-10 rounded-2xl ${color} text-white flex items-center justify-center`}>
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                  </span>
                  <span className="text-[11px] font-mono font-semibold text-blue-600">
                    STEP {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h4 className="text-xl font-black text-[#0b1f3a] mb-2">{name}</h4>
                <p className="text-sm text-[#0b1f3a]/65 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          {/* Daily cycle diagram — dark navy band */}
          <div className="rounded-3xl bg-[#0b1f3a] text-white p-7 md:p-10 mb-12">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-3 text-center">
              The daily cycle
            </p>
            <h3 className="text-2xl md:text-3xl font-black text-center mb-7 leading-tight">
              Each cycle{' '}
              <em className="italic font-light text-blue-300">builds on the last.</em>
            </h3>
            <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
              <span className="bg-blue-500 px-4 py-2 rounded-xl text-sm md:text-base font-semibold">Learning</span>
              <ArrowRight className="w-4 h-4 text-blue-300" />
              <span className="bg-emerald-500 px-4 py-2 rounded-xl text-sm md:text-base font-semibold">Practice</span>
              <ArrowRight className="w-4 h-4 text-blue-300" />
              <span className="bg-violet-500 px-4 py-2 rounded-xl text-sm md:text-base font-semibold">Reflection</span>
              <ArrowRight className="w-4 h-4 text-blue-300" />
              <span className="bg-amber-500 px-4 py-2 rounded-xl text-sm md:text-base font-semibold">Progress</span>
              <RotateCcw className="w-4 h-4 text-blue-300" />
            </div>
            <p className="text-center text-blue-100/80 mt-7 text-sm">
              Each step is simple, but repeated over time it creates meaningful change.
            </p>
          </div>

          {/* Why rhythm matters */}
          <div className="max-w-3xl mx-auto text-center space-y-4 text-lg text-[#0b1f3a]/75 leading-relaxed">
            <p className="font-bold text-[#0b1f3a] text-xl">Real growth does not come from occasional effort.</p>
            <p>It comes from small actions repeated again and again.</p>
            <p>
              When development becomes part of a daily rhythm, improvement stops feeling like a
              project and starts becoming a way of life.
            </p>
            <p className="pt-6 text-2xl md:text-3xl font-black tracking-tight text-[#0b1f3a]">
              Levanta is built to fit into{' '}
              <em className="italic font-light text-blue-600">everyday life.</em>
            </p>
            <p className="text-[#0b1f3a]/65">
              The most powerful changes are created through small actions done consistently over time.
            </p>
          </div>
        </div>
      </section>

      {/* ===== 05 · ENVIRONMENT (COMMUNITY) ===== */}
      <section className="px-6 py-20 md:py-24 bg-[#f4f8fc]">
        <div className="max-w-6xl mx-auto">
          <SectionHead
            eyebrow="Section 05 · Environment"
            title="Growth is easier when"
            italic="you are not alone."
          />

          <div className="max-w-3xl mx-auto space-y-5 text-[#0b1f3a]/80 text-lg leading-relaxed mb-14">
            <p className="font-bold text-[#0b1f3a]">Personal development often becomes a lonely process.</p>
            <p>
              People start with motivation, but without the right environment it becomes difficult
              to stay consistent. Distractions appear, routines break, and progress slows down.
            </p>
            <p>
              Levanta creates a shared environment where members move through their development
              journey alongside others who are working toward similar goals.
            </p>
            <p className="font-bold text-[#0b1f3a]">
              The purpose of the community is not entertainment or endless discussion. It exists to
              support progress.
            </p>
          </div>

          {/* 4 community pillars */}
          <div className="grid md:grid-cols-2 gap-5 mb-12">
            {COMMUNITY.map(({ Icon, color, title, body }) => (
              <div
                key={title}
                className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 flex gap-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <div>
                  <h4 className="text-xl font-black text-[#0b1f3a] mb-2">{title}</h4>
                  <p className="text-[#0b1f3a]/65 leading-relaxed text-[15px]">{body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* A focused environment — single editorial card */}
          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-10 max-w-3xl mx-auto">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3">
              A focused environment
            </p>
            <div className="space-y-4 text-[#0b1f3a]/75 leading-relaxed text-lg">
              <p>The Levanta community is designed differently from traditional social platforms.</p>
              <p className="font-bold text-[#0b1f3a]">
                It is not built around endless scrolling or constant notifications.
              </p>
              <p>
                Instead, it supports meaningful interaction connected to growth, learning, and
                real-life application.
              </p>
              <p>The goal is to create an environment where development becomes easier to sustain over time.</p>
            </div>
          </div>

          <div className="text-center max-w-3xl mx-auto mt-12 space-y-3">
            <p className="text-2xl md:text-3xl font-black tracking-tight text-[#0b1f3a]">
              Individual journeys.{' '}
              <em className="italic font-light text-blue-600">Shared environment.</em>
            </p>
            <p className="text-[#0b1f3a]/70 leading-relaxed">
              Personal development is personal — but it doesn&rsquo;t have to be isolated. Levanta
              brings people together in a place where growth is understood, supported, and shared.
            </p>
          </div>
        </div>
      </section>

      {/* ===== 06 · REINFORCEMENT (GAMIFICATION) ===== */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <SectionHead
            eyebrow="Section 06 · Reinforcement"
            title="Progress"
            italic="you can see."
          />

          <div className="max-w-3xl mx-auto space-y-5 text-[#0b1f3a]/80 text-lg leading-relaxed mb-14">
            <p>
              Developing new skills takes time. When progress is visible, it becomes easier to stay
              engaged and keep moving forward.
            </p>
            <p>
              Levanta includes a reinforcement layer that helps members track their development
              and recognize milestones along the way.
            </p>
            <p className="font-bold text-[#0b1f3a]">
              This system is not about competition. It exists to make progress{' '}
              <em className="italic font-light text-blue-600">visible and rewarding.</em>
            </p>
          </div>

          {/* 4 reinforcement elements */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {REINFORCEMENT.map(({ Icon, color, title, body }) => (
              <div
                key={title}
                className="rounded-3xl bg-[#fafbfd] ring-1 ring-black/5 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:bg-white"
              >
                <span className={`inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br ${color} text-white items-center justify-center mb-4 shadow-sm`}>
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <h4 className="text-lg font-black text-[#0b1f3a] mb-1.5">{title}</h4>
                <p className="text-sm text-[#0b1f3a]/65 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          {/* Progress preview card — dark navy showcase */}
          <div className="rounded-3xl bg-gradient-to-br from-[#0b1f3a] to-[#102a4f] text-white p-7 md:p-10 max-w-2xl mx-auto shadow-2xl">
            <div className="flex items-center justify-between mb-7">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-white/10 ring-1 ring-white/20 rounded-2xl flex items-center justify-center">
                  <Star className="w-7 h-7 text-amber-300 fill-amber-300" />
                </div>
                <div>
                  <p className="text-blue-200 text-xs uppercase tracking-widest">Current level</p>
                  <p className="text-2xl font-black">Level 5</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-blue-200 text-xs uppercase tracking-widest">Total XP</p>
                <p className="text-2xl font-black">2,450</p>
              </div>
            </div>

            <div className="mb-7">
              <div className="flex justify-between text-xs text-blue-200 mb-2">
                <span>Progress to Level 6</span>
                <span>80%</span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-300 rounded-full"
                  style={{ width: '80%' }}
                />
              </div>
            </div>

            <div className="space-y-2.5">
              <div className="flex items-center gap-3 bg-white/5 ring-1 ring-white/10 rounded-2xl p-3">
                <span className="w-9 h-9 rounded-full bg-amber-400 text-amber-900 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4" strokeWidth={2.5} />
                </span>
                <div>
                  <p className="font-semibold">+120 XP earned</p>
                  <p className="text-blue-200 text-xs">Completed: Active Listening</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/5 ring-1 ring-white/10 rounded-2xl p-3">
                <span className="w-9 h-9 rounded-full bg-violet-400 text-violet-900 flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-4 h-4" strokeWidth={2.5} />
                </span>
                <div>
                  <p className="font-semibold">Achievement unlocked</p>
                  <p className="text-blue-200 text-xs">7-Day Consistency Streak</p>
                </div>
              </div>
            </div>

            <p className="text-center text-blue-200 mt-7 text-sm">
              Progress inside Levanta is{' '}
              <span className="text-white font-semibold">measurable and trackable.</span>
            </p>
          </div>

          {/* Why reinforcement matters */}
          <div className="text-center max-w-3xl mx-auto mt-14 space-y-3">
            <p className="text-2xl md:text-3xl font-black tracking-tight text-[#0b1f3a]">
              When progress is visible,{' '}
              <em className="italic font-light text-blue-600">effort feels meaningful.</em>
            </p>
            <p className="text-[#0b1f3a]/70 leading-relaxed">
              Instead of wondering whether improvement is happening, members can see their
              advancement step by step. This makes it easier to stay consistent and continue
              developing over time.
            </p>
            <p className="pt-4 text-[#0b1f3a]/65 leading-relaxed">
              Growth often happens slowly, but when progress is visible, the journey becomes easier
              to sustain. Levanta makes development something you can see, measure, and continue
              building over time.
            </p>
          </div>
        </div>
      </section>

      {/* ===== JOURNEY SUMMARY ===== */}
      <section className="px-6 py-20 md:py-24 bg-gradient-to-b from-[#eaf3fb] to-white">
        <div className="max-w-5xl mx-auto">
          <SectionHead
            eyebrow="The whole architecture"
            title="From philosophy to"
            italic="visible progress."
            sub="Levanta is a system, not just content."
          />

          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-10">
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-2">
              {JOURNEY_STEPS.map(({ Icon, color, label }, i) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center group">
                    <span
                      className={`w-16 h-16 rounded-2xl ${color} flex items-center justify-center shadow-sm transition-transform duration-300 group-hover:-translate-y-0.5`}
                    >
                      <Icon className="w-7 h-7" strokeWidth={2.25} />
                    </span>
                    <span className="mt-3 text-sm font-semibold text-[#0b1f3a]">{label}</span>
                  </div>
                  {i < JOURNEY_STEPS.length - 1 && (
                    <ChevronDown className="w-5 h-5 text-blue-400 md:rotate-[-90deg]" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            Your journey{' '}
            <em className="italic font-light text-blue-300">starts here.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            Now the only step left is to begin. Download the app and start exploring the worlds.
          </p>
          <div className="mt-10">
            <Link
              to="/download-app"
              data-testid="hiw-cta-download"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Download Levanta
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default HowItWorks;
